/**
 * Recruitment ATS — small presentational badges & chips.
 * UPPERCASE colored status badges to match the dashboard's badge style.
 */

import React from 'react';
import { Lock } from 'lucide-react';
import { cn } from '../../../utils/cn';
import type { Stage, JobStatus } from '../../../services/recruitmentApi';
import { STAGE_META, JOB_STATUS_META, sourceLabel } from '../config/constants';
import { initials } from '../utils/format';

const badgeBase =
	'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide leading-tight';

export const StageBadge: React.FC<{ stage: Stage | string; className?: string }> = ({ stage, className }) => {
	const meta = STAGE_META[stage as Stage];
	return (
		<span className={cn(badgeBase, meta?.tone.badge ?? 'bg-gray-100 text-gray-700 border-gray-200', className)}>
			{meta?.label ?? stage}
		</span>
	);
};

export const JobStatusBadge: React.FC<{ status: JobStatus | string; className?: string }> = ({ status, className }) => {
	const meta = JOB_STATUS_META[status as JobStatus];
	return (
		<span className={cn(badgeBase, meta?.badge ?? 'bg-gray-100 text-gray-600 border-gray-200', className)}>
			{meta?.label ?? status}
		</span>
	);
};

/** Source is a neutral tag (not uppercase) — it's data, not a status. */
export const SourcePill: React.FC<{ source: string; className?: string }> = ({ source, className }) => (
	<span
		className={cn(
			'inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600',
			className
		)}
	>
		{sourceLabel(source)}
	</span>
);

/** 🔒 Restricted — shown when a CTC field is absent for the current user (rule R6). */
export const RestrictedChip: React.FC<{ className?: string; label?: string }> = ({ className, label = 'Restricted' }) => (
	<span
		className={cn(
			'inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500',
			className
		)}
		title='You do not have permission to view compensation details'
	>
		<Lock className='h-3 w-3' />
		{label}
	</span>
);

export const Avatar: React.FC<{ name: string | null | undefined; className?: string }> = ({ name, className }) => (
	<span
		className={cn(
			'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700',
			className
		)}
		aria-hidden
	>
		{initials(name)}
	</span>
);
